import {
  ApartmentOutlined,
  CodeOutlined,
  EnvironmentOutlined,
  NumberOutlined,
} from "@ant-design/icons";
import React from "react";

// Tag definitions with label, color, and icon type
type CorrelationTagType =
  | "hostname"
  | "partial_hostname"
  | "ipv4"
  | "ipv6"
  | "arn"
  | "mac_address"
  | "serial_number"
  | "fqdn"
  | "cloud_resource_id_match";
export type CorrelationTagIconType =
  | "hostname"
  | "ip"
  | "arn"
  | "mac"
  | "serial_number"
  | "fqdn"
  | "cloud_resource_id_match";

const CORRELATION_TAG_MAP: Record<
  CorrelationTagType,
  {
    label: string;
    color: "green" | "blue" | "orange";
    iconType: CorrelationTagIconType;
  }
> = {
  hostname: { label: "Hostname", color: "blue", iconType: "hostname" },
  partial_hostname: {
    label: "Partial Hostname",
    color: "blue",
    iconType: "hostname",
  },
  ipv4: { label: "IPv4", color: "green", iconType: "ip" },
  ipv6: { label: "IPv6", color: "green", iconType: "ip" },
  arn: { label: "ARN", color: "orange", iconType: "arn" },
  mac_address: { label: "MAC", color: "green", iconType: "mac" },
  serial_number: {
    label: "Serial Number",
    color: "green",
    iconType: "serial_number",
  },
  fqdn: {
    label: "FQDN",
    color: "green",
    iconType: "fqdn",
  },
  cloud_resource_id_match: {
    label: "Cloud Resource ID Match",
    color: "orange",
    iconType: "cloud_resource_id_match",
  },
};

// Mapping from correlatedValues keys (camelCase from API) to tag types
export type CorrelatedValueKey =
  | "hostname"
  | "partialHostname"
  | "ipv4"
  | "ipv6"
  | "arn"
  | "macAddress"
  | "serialNumber"
  | "fqdn"
  | "cloudResourceId";

const CORRELATED_VALUE_KEY_TO_TAG_TYPE: Record<
  CorrelatedValueKey,
  CorrelationTagType
> = {
  hostname: "hostname",
  partialHostname: "partial_hostname",
  ipv4: "ipv4",
  ipv6: "ipv6",
  arn: "arn",
  macAddress: "mac_address",
  serialNumber: "serial_number",
  fqdn: "fqdn",
  cloudResourceId: "cloud_resource_id_match",
};

// Export keys array for iteration
export const CORRELATED_VALUE_KEYS = Object.keys(
  CORRELATED_VALUE_KEY_TO_TAG_TYPE,
) as CorrelatedValueKey[];

// Get label for a correlatedValues key
export const getCorrelatedValueLabel = (key: CorrelatedValueKey): string => {
  const tagType = CORRELATED_VALUE_KEY_TO_TAG_TYPE[key];
  return CORRELATION_TAG_MAP[tagType]?.label || key;
};

// Rule to tags mapping
const CORRELATION_RULE_TAGS_MAP: Record<string, CorrelationTagType[]> = {
  hostname_and_ipv4_match: ["hostname", "ipv4"],
  hostname_and_ipv6_match: ["hostname", "ipv6"],
  hostname_and_mac_address_match: ["hostname", "mac_address"],
  hostname_match: ["hostname"],
  partial_hostname_match: ["partial_hostname"],
  partial_hostname_and_ipv4_match: ["partial_hostname", "ipv4"],
  partial_hostname_and_ipv6_match: ["partial_hostname", "ipv6"],
  arn_match: ["arn"],
  serial_number_match: ["serial_number"],
  fqdn_and_ipv4_match: ["fqdn", "ipv4"],
  fqdn_and_ipv6_match: ["fqdn", "ipv6"],
  fqdn_match: ["fqdn"],
  cloud_resource_id_match: ["cloud_resource_id_match"],
};

// Icon component map for correlation tags
export const CORRELATION_TAG_ICONS: Record<
  CorrelationTagIconType,
  React.ReactNode
> = {
  hostname: <ApartmentOutlined />,
  ip: <EnvironmentOutlined />,
  arn: <CodeOutlined />,
  mac: <EnvironmentOutlined />,
  serial_number: <NumberOutlined />,
  fqdn: <ApartmentOutlined />,
  cloud_resource_id_match: <CodeOutlined />,
};

export type CorrelationTagInfo = {
  label: string;
  color: "green" | "blue" | "orange";
  iconType: CorrelationTagIconType;
};

// Helper to get tags for a correlation rule
export const getCorrelationRuleTags = (
  rule: string | null,
): CorrelationTagInfo[] | null => {
  if (!rule) return null;
  const tagTypes = CORRELATION_RULE_TAGS_MAP[rule];
  if (!tagTypes) return null;
  return tagTypes.map((tagType) => CORRELATION_TAG_MAP[tagType]);
};
